import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function ContactForm() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const data = new URLSearchParams();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('subject', formData.subject);
      data.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d7t27iaj1g8i0g4g5ql0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }

    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <section ref={ref} className="w-full bg-brand-cream py-20 md:py-28">
      <div className="px-6 lg:px-[7%]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center" style={style}>
            <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
              Escríbenos
            </span>
            <h2 className="font-serif text-brand-black text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.15] mt-4">
              Tu mensaje <span className="italic text-brand-gray-300">directo al taller</span>
            </h2>
            <p className="font-sans text-brand-gray-500 text-base leading-[1.8] mt-5 max-w-lg mx-auto">
              Cuéntanos qué necesitas. Alta costura, arreglos, consultas sobre la tienda o
              simplemente quieres conocernos. Respondemos en menos de 24 horas.
            </p>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-14 bg-white rounded-3xl p-8 md:p-12 lg:p-16"
            style={isVisible ? {
              opacity: 1,
              transform: 'translateY(0px)',
              transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
            } : {
              opacity: 0,
              transform: 'translateY(36px)',
              transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
              willChange: 'opacity, transform',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contact-name" className="block font-sans text-brand-black text-xs tracking-wider uppercase mb-2">
                  Nombre completo
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="María García"
                  className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-md px-5 py-3.5 font-sans text-brand-black text-sm placeholder:text-brand-gray-400 focus:outline-none focus:border-brand-black/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block font-sans text-brand-black text-xs tracking-wider uppercase mb-2">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="maria@email.com"
                  className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-md px-5 py-3.5 font-sans text-brand-black text-sm placeholder:text-brand-gray-400 focus:outline-none focus:border-brand-black/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block font-sans text-brand-black text-xs tracking-wider uppercase mb-2">
                  Teléfono
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+34 600 000 000"
                  className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-md px-5 py-3.5 font-sans text-brand-black text-sm placeholder:text-brand-gray-400 focus:outline-none focus:border-brand-black/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className="block font-sans text-brand-black text-xs tracking-wider uppercase mb-2">
                  Asunto
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-md px-5 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black/40 transition-colors appearance-none cursor-pointer"
                >
                  <option value="">Selecciona un asunto</option>
                  <option value="alta-costura">Consulta Alta Costura</option>
                  <option value="arreglos">Arreglos / Prêt-à-porter</option>
                  <option value="tienda">Tienda Online</option>
                  <option value="asesoria">Asesoría de Imagen</option>
                  <option value="prensa">Prensa / Colaboraciones</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="contact-message" className="block font-sans text-brand-black text-xs tracking-wider uppercase mb-2">
                Mensaje
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={5}
                placeholder="Cuéntanos en qué podemos ayudarte..."
                className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-md px-5 py-3.5 font-sans text-brand-black text-sm placeholder:text-brand-gray-400 focus:outline-none focus:border-brand-black/40 transition-colors resize-none"
              />
              <p className="font-sans text-brand-gray-400 text-xs mt-2 text-right">
                {formData.message.length}/500
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="bg-brand-black text-white font-sans text-[13px] tracking-widest uppercase px-10 py-4 rounded-md hover:bg-brand-gray-800 transition-colors duration-300 whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? 'Enviando...' : 'Enviar mensaje'}
              </button>

              {status === 'success' && (
                <span className="font-sans text-emerald-600 text-sm flex items-center gap-2">
                  <i className="ri-check-line" />
                  Mensaje enviado. Te contactaremos pronto.
                </span>
              )}
              {status === 'error' && (
                <span className="font-sans text-red-500 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line" />
                  Error al enviar. Inténtalo de nuevo.
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}